// frontend/src/components/pantallaInformativa/ColaEspera.js
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { apellidoNombre } from '../../utils/nombrePersona';

const SEGUNDOS_POR_PAGINA = 10;

const estaEnAnses = (turno) => turno?.estado === 'EN_ANSES';

/**
 * Una cola de la pantalla informativa: título, cantidad de personas esperando y las tarjetas en
 * orden. Si no entran todas, va pasando de página sola, así cada persona termina viendo su nombre.
 * Cuántas entran por página se mide en pantalla, porque depende del TV.
 *
 * Las tarjetas no llevan número de lugar: los clientes lo confundían con un número de turno.
 *
 * En la cola ANSES vienen al final los que ya salieron con el abogado hacia el organismo
 * (EN_ANSES, así los ordena el backend): se muestran en gris, porque ya no esperan.
 *
 * Cuando comparte el lugar con otras colas (ver ColaRotativa), alCompletarRonda se llama cuando ya
 * mostró todas sus páginas, para pasar a la siguiente.
 *
 * El tamaño no depende de cuánta gente hay: las colas que se ven en pantalla van mitad y mitad.
 */
function ColaEspera({ titulo, icono, turnos, alCompletarRonda }) {
    const listaRef = useRef(null);
    const [porPagina, setPorPagina] = useState(5);
    const [pagina, setPagina] = useState(0);
    // En refs para que el intervalo de las páginas no se reinicie con cada render.
    const paginaRef = useRef(0);
    const alCompletarRondaRef = useRef(alCompletarRonda);
    alCompletarRondaRef.current = alCompletarRonda;
    const rota = Boolean(alCompletarRonda);

    const cantidad = turnos.length;
    const cantidadEnAnses = useMemo(() => turnos.filter(estaEnAnses).length, [turnos]);
    const cantidadEsperando = cantidad - cantidadEnAnses;
    const hayTurnos = cantidad > 0;
    const totalPaginas = Math.max(1, Math.ceil(cantidad / porPagina));
    const paginaVisible = Math.min(pagina, totalPaginas - 1);

    // Mide cuántas tarjetas entran en el alto disponible. Se mide sobre una tarjeta real (hay al
    // menos una cuando importa paginar) y se vuelve a medir si cambia el tamaño de la pantalla.
    useLayoutEffect(() => {
        const lista = listaRef.current;
        if (!lista || !hayTurnos) return undefined;

        const medir = () => {
            const tarjeta = lista.querySelector('.pi-tarjeta');
            if (!tarjeta) return;
            const separacion = parseFloat(window.getComputedStyle(lista).rowGap) || 0;
            const altoTarjeta = tarjeta.getBoundingClientRect().height + separacion;
            if (altoTarjeta <= 0) return;
            const entran = Math.max(1, Math.floor((lista.clientHeight + separacion) / altoTarjeta));
            setPorPagina((anterior) => (anterior === entran ? anterior : entran));
        };

        medir();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observador = new ResizeObserver(medir);
        observador.observe(lista);
        return () => observador.disconnect();
    }, [hayTurnos]);

    useEffect(() => {
        paginaRef.current = paginaVisible;
    }, [paginaVisible]);

    useEffect(() => {
        if (totalPaginas <= 1) {
            setPagina(0);
            if (!rota) return undefined;
            // Con una sola página, la ronda es lo que dura una página.
            const espera = setTimeout(() => alCompletarRondaRef.current?.(), SEGUNDOS_POR_PAGINA * 1000);
            return () => clearTimeout(espera);
        }
        const intervalo = setInterval(() => {
            const siguiente = (paginaRef.current + 1) % totalPaginas;
            if (siguiente === 0 && alCompletarRondaRef.current) {
                alCompletarRondaRef.current();
                return;
            }
            paginaRef.current = siguiente;
            setPagina(siguiente);
        }, SEGUNDOS_POR_PAGINA * 1000);
        return () => clearInterval(intervalo);
    }, [totalPaginas, rota]);

    const desde = paginaVisible * porPagina;
    const visibles = turnos.slice(desde, desde + porPagina);

    return (
        <section className="pi-cola">
            <header className="pi-cola__encabezado">
                <h2 className="pi-cola__titulo">
                    <i className={`bi ${icono}`} aria-hidden="true" />
                    {titulo}
                </h2>
                <span className="pi-cola__resumen">
                    {totalPaginas > 1 && (
                        <span className="pi-cola__paginas" aria-hidden="true">
                            {Array.from({ length: totalPaginas }, (_, i) => (
                                <span
                                    key={i}
                                    className={`pi-cola__punto${i === paginaVisible ? ' pi-cola__punto--activo' : ''}`}
                                />
                            ))}
                        </span>
                    )}
                    {(cantidadEsperando > 0 || cantidadEnAnses === 0) && (
                        <span className="pi-cola__cantidad">{cantidadEsperando} en espera</span>
                    )}
                    {cantidadEnAnses > 0 && (
                        <span className="pi-cola__cantidad">{cantidadEnAnses} en ANSES</span>
                    )}
                </span>
            </header>

            {hayTurnos ? (
                <ol className="pi-cola__lista" ref={listaRef}>
                    {visibles.map((turno, indice) => {
                        const enAnses = estaEnAnses(turno);
                        // Los EN_ANSES van al final: el primero de la lista es el próximo a atender.
                        const clase = enAnses
                            ? ' pi-tarjeta--en-anses'
                            : desde + indice === 0 ? ' pi-tarjeta--primera' : '';
                        return (
                            <li className={`pi-tarjeta${clase}`} key={turno.idTurno ?? `${desde + indice}`}>
                                <span className="pi-tarjeta__datos">
                                    <span className="pi-tarjeta__nombre">
                                        {apellidoNombre(turno.apellido, turno.nombre) || 'SIN NOMBRE'}
                                    </span>
                                    <span className="pi-tarjeta__tramite">{turno.tipoTramite}</span>
                                </span>
                                {enAnses && <span className="pi-tarjeta__estado">En ANSES</span>}
                            </li>
                        );
                    })}
                </ol>
            ) : (
                <p className="pi-cola__vacia">No hay personas esperando</p>
            )}
        </section>
    );
}

export default ColaEspera;
